import { FaUndo } from 'react-icons/fa';
import { POLICY_STATUS_ORDER, STATUS_META } from '../../PolicyAdmin/constants';
import type { PolicyStatus } from '../../PolicyAdmin/types';
import { Button } from '../../PolicyAdmin/components/Button';
import { useReportingStore } from '../reportingStore';
import { PERIOD_PRESETS, type PeriodPresetKey } from '../utils';
import { MultiSelect } from './MultiSelect';

const FIELD_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--color-background-paper)',
  borderColor: 'var(--color-border-light)',
  color: 'var(--color-text-primary)',
};

const STATUS_OPTIONS = POLICY_STATUS_ORDER.map((status: PolicyStatus) => ({
  value: status,
  label: STATUS_META[status].label,
}));

/**
 * Shared filter row for both reporting pages. Filters live in the store, so the
 * analytics view and the bordereau export always describe the same slice of the
 * book — you cannot chart one period and export another by accident.
 */
export const FilterBar: React.FC = () => {
  const filters = useReportingStore(s => s.filters);
  const periodPreset = useReportingStore(s => s.periodPreset);
  const reference = useReportingStore(s => s.reference);
  const setFilters = useReportingStore(s => s.setFilters);
  const setPeriodPreset = useReportingStore(s => s.setPeriodPreset);
  const clearFilters = useReportingStore(s => s.clearFilters);
  const loading = useReportingStore(s => s.loading);

  const isFiltered =
    filters.products.length > 0 ||
    filters.carrierIds.length > 0 ||
    filters.brokerIds.length > 0 ||
    filters.statuses.length > 0 ||
    periodPreset !== 'last_12m';

  return (
    <div
      className="mb-6 rounded-xl border p-4"
      style={{
        backgroundColor: 'var(--color-background-paper)',
        borderColor: 'var(--color-border-light)',
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Period
          </span>
          <select
            value={periodPreset}
            onChange={e => setPeriodPreset(e.target.value as PeriodPresetKey)}
            className="rounded-lg border px-3 py-2 text-sm"
            style={FIELD_STYLE}
          >
            {PERIOD_PRESETS.map(preset => (
              <option key={preset.key} value={preset.key}>
                {preset.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Written from
          </span>
          <input
            type="date"
            value={filters.fromDate}
            max={filters.toDate}
            onChange={e => setFilters({ fromDate: e.target.value })}
            className="rounded-lg border px-3 py-2 text-sm"
            style={FIELD_STYLE}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Written to
          </span>
          <input
            type="date"
            value={filters.toDate}
            min={filters.fromDate}
            onChange={e => setFilters({ toDate: e.target.value })}
            className="rounded-lg border px-3 py-2 text-sm"
            style={FIELD_STYLE}
          />
        </label>

        <MultiSelect
          label="Product"
          allLabel="All products"
          options={reference.products.map(product => ({ value: product, label: product }))}
          selected={filters.products}
          onChange={products => setFilters({ products })}
        />

        <MultiSelect
          label="Carrier"
          allLabel="All carriers"
          options={reference.carriers.map(carrier => ({ value: carrier.id, label: carrier.name }))}
          selected={filters.carrierIds}
          onChange={carrierIds => setFilters({ carrierIds })}
        />

        <MultiSelect
          label="Broker"
          allLabel="All brokers"
          options={reference.brokers.map(broker => ({
            value: broker.id,
            label: broker.agency,
            detail: broker.name,
          }))}
          selected={filters.brokerIds}
          onChange={brokerIds => setFilters({ brokerIds })}
        />

        <MultiSelect
          label="Status"
          allLabel="All statuses"
          options={STATUS_OPTIONS}
          selected={filters.statuses}
          onChange={statuses => setFilters({ statuses: statuses as PolicyStatus[] })}
        />

        {isFiltered && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<FaUndo className="h-3 w-3" />}
            onClick={clearFilters}
            disabled={loading}
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
};
